import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import { Skeleton } from "@lawline/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { formatDateTime } from "@platform/fmt";
import { webhookEventsQueryOptions } from "../webhooks.queries";
import type { WebhookEndpoint } from "../webhooks.types";
import { endpointHost } from "../webhooks.utils";
import { WebhookDeliveryDetail } from "./webhook-delivery-detail";
import { WebhookDeliveryStatusBadge } from "./webhook-delivery-status-badge";

// The organization's events across its endpoints. A delivery opens the same dialog as in the
// endpoint's own log, where it can be sent again.
export function WebhookEventsTable({ endpoints }: { endpoints: WebhookEndpoint[] }) {
	const t = useTranslations("webhooks.events");
	const events = useInfiniteQuery(webhookEventsQueryOptions);
	const [open, setOpen] = useState<{ endpoint: WebhookEndpoint; deliveryId: string } | null>(null);
	const rows = events.data?.pages.flatMap((page) => page.data) ?? [];

	return (
		<section className="flex flex-col gap-3">
			<div className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold">{t("title")}</h2>
				<p className="text-muted-foreground text-sm">{t("description")}</p>
			</div>

			{events.isPending ? (
				<Skeleton className="h-24 w-full" />
			) : events.error ? (
				<p role="alert" className="text-destructive text-sm">
					{t("error")}
				</p>
			) : (
				<div className="overflow-x-auto rounded-md border contain-inline-size">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("columns.event")}</TableHead>
								<TableHead className="hidden sm:table-cell">{t("columns.run")}</TableHead>
								<TableHead className="hidden md:table-cell">{t("columns.created")}</TableHead>
								<TableHead>{t("columns.deliveries")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} className="text-muted-foreground h-20 text-center text-sm">
										{t("emptyState")}
									</TableCell>
								</TableRow>
							) : (
								rows.map((event) => (
									<TableRow key={event.id}>
										<TableCell className="font-mono text-xs font-medium">{event.type}</TableCell>
										<TableCell className="hidden font-mono text-xs sm:table-cell">
											{event.runId ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span>{event.runId.slice(0, 8)}</span>
													</TooltipTrigger>
													<TooltipContent>{event.runId}</TooltipContent>
												</Tooltip>
											) : (
												"-"
											)}
										</TableCell>
										<TableCell className="hidden md:table-cell">
											{formatDateTime(event.createdAt)}
										</TableCell>
										<TableCell>
											<div className="flex flex-col items-start gap-1">
												{event.deliveries.length === 0
													? "-"
													: event.deliveries.map((delivery) => {
															const endpoint = endpoints.find(
																(row) => row.id === delivery.endpointId,
															);
															return (
																<button
																	key={delivery.id}
																	type="button"
																	disabled={!endpoint}
																	onClick={() =>
																		endpoint && setOpen({ endpoint, deliveryId: delivery.id })
																	}
																	className="flex max-w-64 items-center gap-1.5 hover:underline disabled:no-underline"
																>
																	<WebhookDeliveryStatusBadge status={delivery.status} />
																	<span className="truncate font-mono text-xs">
																		{endpointHost(delivery.endpointUrl)}
																	</span>
																</button>
															);
														})}
											</div>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</div>
			)}

			{events.hasNextPage && (
				<Button
					variant="outline"
					size="sm"
					className="self-center"
					disabled={events.isFetchingNextPage}
					onClick={() => void events.fetchNextPage()}
				>
					{t("loadMore")}
				</Button>
			)}

			{open && (
				<WebhookDeliveryDetail
					endpoint={open.endpoint}
					deliveryId={open.deliveryId}
					onClose={() => setOpen(null)}
				/>
			)}
		</section>
	);
}
